import type {
  CompanyDiscoveryAiOutput,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

/**
 * How to resolve and apply global-assistant "route filter" pre-fetch for a
 * navigation target. Inline CustomDash AI search already branches screener vs TD;
 * the global assistant must mirror that for the queued pre-resolve path.
 */
export type GlobalAssistantRouteFilterKind = 'tdV2' | 'screener';

/** Result of pre-resolving filters before navigation; discriminated for apply. */
export type GlobalAssistantResolvedRouteFilters =
  | { filterKind: 'tdV2'; response: TalentDiscoveryAiFilterSearchResponseV2 }
  | { filterKind: 'screener'; response: CompanyDiscoveryAiOutput };

/**
 * Standalone Talent Discovery product (`/talent-discovery`).
 */
export function isTalentDiscoveryPublicPath(navigatePath: string): boolean {
  const n = navigatePath.split('/').filter(Boolean);
  return n.length === 1 && n[0] === 'talent-discovery';
}

/**
 * Company / geography / role Discovery (screener) — dedicated AI endpoint+transform.
 */
export function isScreenerV2DashboardPath(navigatePath: string): boolean {
  const n = navigatePath.split('/').filter(Boolean);
  return n.length === 2 && n[1] === 'screener';
}

export function getGlobalAssistantRouteFilterKind(
  navigatePath: string
): GlobalAssistantRouteFilterKind {
  if (isScreenerV2DashboardPath(navigatePath)) {
    return 'screener';
  }
  return 'tdV2';
}

/**
 * SHAPE guards for the two resolved-filter artifacts.
 *
 * The `filterKind` on a `GlobalAssistantResolvedRouteFilters` is PATH-derived
 * (from the navigate target via `getGlobalAssistantRouteFilterKind`). When the
 * resolved `response` SHAPE disagrees with that path — e.g. a screener artifact
 * resolved for a route classified as `tdV2` — the applier must follow the SHAPE,
 * not the path, or the response is handed to the wrong applier and corrupts the
 * apply. These guards narrow by the distinctive top-level `filters` member:
 *   - screener `CompanyDiscoveryAiOutput` → `filters.entity_info_selectors`
 *   - tdV2 `TalentDiscoveryAiFilterSearchResponseV2` → `filters.expr`
 */
function getFiltersRecord(response: unknown): Record<string, unknown> | null {
  if (typeof response !== 'object' || response === null) {
    return null;
  }
  const filters = (response as { filters?: unknown }).filters;
  if (typeof filters !== 'object' || filters === null) {
    return null;
  }
  return filters as Record<string, unknown>;
}

/** Screener-shaped: `filters.entity_info_selectors` is present. */
export function isScreenerAiOutput(
  response: unknown
): response is CompanyDiscoveryAiOutput {
  const filters = getFiltersRecord(response);
  return filters !== null && 'entity_info_selectors' in filters;
}

/** tdV2-shaped: `filters.expr` (the `UserNode` expression tree) is present. */
export function isTalentDiscoveryFilterResponse(
  response: unknown
): response is TalentDiscoveryAiFilterSearchResponseV2 {
  const filters = getFiltersRecord(response);
  return filters !== null && 'expr' in filters;
}
